import React, { useState, useEffect } from 'react';
import { isNil } from 'lodash';
// Components
import Modal from 'shared/modals/Modal';
import { ListView } from '@progress/kendo-react-listview';
import { Sortable } from '@progress/kendo-react-sortable';
import Toggle from 'shared/inputs/BaseToggle';
import Select from 'shared/inputs/BaseSelect';
import BaseButton, { ResetButton } from 'shared/buttons';
import { RiDraggable } from 'react-icons/ri';
// Styles
import './style.scss';
import { theme } from 'src/theme';
import { SelectAllButton } from 'shared/buttons/SelectAllButton';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const ColumnsToDisplayModal = ({
  isOpen,
  close,
  columnsToDisplay = [],
  onColumnDisplayingChanged,
  onResetColumnsOrder = null,
  onColumnsOrderChanged = null,
  getViewColumns = null,
  sortable = false,
  views = null,
}) => {
  const allChecked = () => {
    let result = true;
    columnsToDisplay.forEach((column) => {
      if (!column.selected) {
        result = false;
      }
    });
    return result;
  };

  const [showSelectAll, setShowSelectAll] = useState(!allChecked());

  const handleColumnCheckedChanged = (checked, dataItem, refresh = true) => {
    onColumnDisplayingChanged(dataItem.id, checked, refresh, dataItem.index ?? 0);
  };

  const handleSelectAll = () => {
    columnsToDisplay.forEach((column) => {
      handleColumnCheckedChanged(true, column);
    });
  };

  const handleDeselectAll = () => {
    columnsToDisplay.forEach((column) => {
      if (!column.required) {
        handleColumnCheckedChanged(false, column);
      }
    });
  };

  useEffect(() => {
    setShowSelectAll(!allChecked());
  }, [columnsToDisplay]);

  const ColumnItemRenderer = ({
    style, attributes, dataItem, forwardRef, ...props
  }) => (
    <>
      <div
        className='toggle-column-wrapper'
        title={dataItem.required ? 'Required column' : undefined}
        style={style}
        {...attributes}
        ref={forwardRef}
        {...props}
      >
        <div>
          <Toggle
            onChange={(event) => handleColumnCheckedChanged(event.value, dataItem)}
            value={dataItem.required || dataItem.selected}
            disabled={dataItem.required}
          />
        </div>
        <div className='column-label'>
          <div className={dataItem.selected ? undefined : 'not-selected-label'}>{dataItem.value}</div>
          {dataItem.required && <div className='required-warning'>Required column</div>}
        </div>
        {sortable && <RiDraggable size={24} className='drag-icon' />}
      </div>
    </>
  );

  const onViewClicked = (data) => {
    const view = data.value.value;
    if (getViewColumns) {
      getViewColumns(view).then((columns) => {
        columnsToDisplay.forEach((column, ix) => {
          const columnId = column.id;
          // make first letter uppercase
          const columnIdUpper = columnId[0].toUpperCase() + columnId.slice(1);
          const foundColumn = columns.find((c) => c === columnIdUpper);

          const isLast = ix === columnsToDisplay.length - 1;
          handleColumnCheckedChanged(!isNil(foundColumn), column, isLast);
        });
      });
    }
  };

  const handleChangeOrder = (newState) => {
    if (onColumnsOrderChanged) {
      onColumnsOrderChanged(newState);
    }
  };

  const onDragOver = (event) => handleChangeOrder(event.newState);
  const onNavigate = (event) => handleChangeOrder(event.newState);

  return (
    <Modal
      isOpen={isOpen}
      handleClose={() => close()}
      title='Select Fields to Show'
      style={{ height: '500px' }}
      containerStyle='columns-to-display-modal'
      autoFocus
    >
      <>
        <div className='select-all-toggle-wrapper'>
          {showSelectAll ? (<SelectAllButton allSelected={false} onClick={handleSelectAll} size='small' />)
            : (<SelectAllButton allSelected onClick={handleDeselectAll} size='small' />)}
          {(sortable && onResetColumnsOrder) ? (<ResetButton title='Reset Ordering' onClick={onResetColumnsOrder} />) : null}
        </div>
        {views && getViewColumns && (
          <div className='view-type-wrapper'>
            <Select label='Views' data={views} textField='value' dataItemKey='id' onChange={onViewClicked} />
          </div>
        )}
        {sortable
          ? (
            <Sortable
              className='toggle-column-list-wrapper'
              idField='id'
              data={columnsToDisplay}
              itemUI={ColumnItemRenderer}
              onDragOver={onDragOver}
              onNavigate={onNavigate}
            />
          )
          : (<ListView data={columnsToDisplay} item={ColumnItemRenderer} />)}
      </>
    </Modal>
  );
};

export default ColumnsToDisplayModal;
